import { gql } from "@apollo/client";

/**
 * Device ↔ Operation mapping (Capability table).
 *
 * Shared by the Bind Device flow and the device edit panel so both write the
 * mapping the same way. A device can be linked to 0 or more operations.
 */

export const GET_OPERATIONS_FOR_PLANT = gql`
    query GetOperationsForCapabilities($where: OperationWhereInput!) {
        operations(where: $where) {
            id
            name
            is_productive
        }
    }
`;

export const GET_DEVICE_CAPABILITIES = gql`
    query GetDeviceCapabilities($deviceId: String!) {
        capabilities(where: { device_id: { equals: $deviceId } }) {
            operation_id
            operation {
                id
                name
            }
        }
    }
`;

export const CREATE_MANY_CAPABILITY = gql`
    mutation CreateManyCapability($data: [CapabilityCreateManyInput!]!) {
        createManyCapability(data: $data, skipDuplicates: true) {
            count
        }
    }
`;

export const DELETE_MANY_CAPABILITY = gql`
    mutation DeleteManyCapability($deviceId: String!, $operationIds: [Int!]!) {
        deleteManyCapability(
            where: {
                device_id: { equals: $deviceId }
                operation_id: { in: $operationIds }
            }
        ) {
            count
        }
    }
`;

export const operationToOption = (operation) => ({
    value: Number(operation.id),
    label: operation.name,
});

/**
 * Persists the operation list for a single device by diffing against what is
 * already linked — only the added and removed rows are touched.
 *
 * @returns {Promise<{added: number, removed: number}>}
 */
export async function saveDeviceCapabilities(
    client,
    deviceId,
    nextOperationIds,
    currentOperationIds = [],
) {
    const next = new Set(nextOperationIds.map(Number));
    const current = new Set(currentOperationIds.map(Number));

    const added = [...next].filter((id) => !current.has(id));
    const removed = [...current].filter((id) => !next.has(id));

    if (removed.length) {
        await client.mutate({
            mutation: DELETE_MANY_CAPABILITY,
            variables: { deviceId, operationIds: removed },
        });
    }

    if (added.length) {
        await client.mutate({
            mutation: CREATE_MANY_CAPABILITY,
            variables: {
                data: added.map((operationId) => ({
                    device_id: deviceId,
                    operation_id: operationId,
                })),
            },
        });
    }

    return { added: added.length, removed: removed.length };
}
